import {useCallback, useEffect, useRef, useState} from 'react'
import {STORAGE_KEYS} from '@/shared/config'

const AUTO_SHOW_DELAY_MS = 5000

/**
 * Controls focus-hint popover state and persists "seen" in localStorage on close.
 * Use in the page that renders the hint button (e.g. RoomPage).
 * Auto-show (requestFocusHint) opens the popover after a short delay so new users aren't startled.
 */
export function useFocusHint() {
  const [focusHintOpen, setFocusHintOpen] = useState(false)
  const autoShowTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    return () => {
      if (autoShowTimeoutRef.current) clearTimeout(autoShowTimeoutRef.current)
    }
  }, [])

  const onFocusHintOpenChange = useCallback((open: boolean) => {
    setFocusHintOpen(open)
    if (!open) {
      if (autoShowTimeoutRef.current) {
        clearTimeout(autoShowTimeoutRef.current)
        autoShowTimeoutRef.current = null
      }
      try {
        localStorage.setItem(STORAGE_KEYS.FOCUS_HINT_SEEN, '1')
      } catch {
        // ignore
      }
    }
  }, [])

  const requestFocusHint = useCallback(() => {
    if (autoShowTimeoutRef.current) clearTimeout(autoShowTimeoutRef.current)
    autoShowTimeoutRef.current = setTimeout(() => {
      autoShowTimeoutRef.current = null
      setFocusHintOpen(true)
    }, AUTO_SHOW_DELAY_MS)
  }, [])

  return {focusHintOpen, onFocusHintOpenChange, requestFocusHint}
}
